/**
 * Size Guide Manager — drawer "Guide des tailles" de la PDP.
 *
 * Ouvert par le bouton "Trouver ma taille" placé au-dessus des tailles.
 * Les données (tailles + lignes de mesures en cm) sont rendues en SSR dans
 * #mm-size-guide-data par la section produit ; ce module ne fait aucun fetch.
 *
 * Le toggle cm/in convertit à l'affichage (1 in = 2.54 cm) — une seule source
 * de données côté Liquid.
 */
const SizeGuideManager = {
	config: {
		selectors: {
			trigger: '.mm-pdp-find-size',
			dataScript: '#mm-size-guide-data',
			template: '#mm-sg-shell-template',
			panel: '.mm-size-guide',
			drawer: '.mm-size-guide__drawer',
			overlay: '.mm-size-guide__overlay',
			close: '.mm-size-guide__close',
			dismiss: '.mm-size-guide__close, .mm-size-guide__dismiss',
			table: '.mm-size-guide__table',
			unitSwitch: '.mm-size-guide__unit-switch',
			unitCm: '.mm-size-guide__unit-label--cm',
			unitIn: '.mm-size-guide__unit-label--in',
			selectedSize: '.mm-pdp-sizes input[data-variant-input]:checked'
		},
		classes: {
			open: 'mm-size-guide--open',
			bodyOpen: 'mm-overflow-hidden',
			unitActive: 'mm-size-guide__unit-label--active',
			cellCurrent: 'mm-size-guide__cell--current',
			valueEmpty: 'mm-size-guide__value--empty'
		},
		focusableSelector:
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
	},

	panel: null,
	data: null,
	unit: 'cm',
	focusTrap: null,

	init() {
		// Le bouton n'existe que sur la PDP d'un produit avec des mesures.
		if (!document.querySelector(this.config.selectors.trigger)) return;

		this._bindEvents();
		window.log('info', 'SizeGuide', 'Initialise');
	},

	_bindEvents() {
		// Délégation : le variant picker est re-rendu au changement de couleur.
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.trigger);
			if (!trigger) return;
			e.preventDefault();
			this.open(trigger);
		});
	},

	/**
	 * Clone la coquille du <template> et cable ses interactions (une seule fois).
	 */
	_create() {
		const tpl = document.querySelector(this.config.selectors.template);
		if (!tpl) {
			window.log('error', 'SizeGuide', 'Template mm-sg-shell-template introuvable');
			return false;
		}

		const el = tpl.content.cloneNode(true).firstElementChild;
		document.body.appendChild(el);
		this.panel = el;

		el.querySelector(this.config.selectors.overlay).addEventListener('click', () => this.close());
		el.querySelectorAll(this.config.selectors.dismiss).forEach((btn) => {
			btn.addEventListener('click', () => this.close());
		});

		const unitSwitch = el.querySelector(this.config.selectors.unitSwitch);
		unitSwitch?.addEventListener('click', () => this._toggleUnit());

		// Empeche le scroll de la page derriere le drawer (Lenis est deja stoppe).
		el.querySelector(this.config.selectors.drawer).addEventListener('wheel', (e) => e.stopPropagation(), {
			passive: true
		});

		return true;
	},

	/**
	 * @param {HTMLElement} trigger - Bouton declencheur (focus restaure a la fermeture)
	 */
	open(trigger) {
		if (!this.panel && !this._create()) return;

		this.data = window.parseJSON(this.config.selectors.dataScript, null);
		if (!this.data || !this.data.rows?.length) {
			window.log('warn', 'SizeGuide', 'Aucune donnee de mesures disponible');
			return;
		}

		this._injectContent();
		this._renderTable();

		const isFirstOpen = !this.panel.classList.contains(this.config.classes.open);
		this.panel.setAttribute('aria-hidden', 'false');
		if (isFirstOpen) void this.panel.offsetWidth; // force reflow : la transition joue au 1er open
		this.panel.classList.add(this.config.classes.open);

		document.body.classList.add(this.config.classes.bodyOpen);
		window.LenisManager?.stop();

		this._setupFocusTrap(trigger);
		window.emit('global:size-guide-open', { handle: this.data.handle });
	},

	close() {
		if (!this.panel) return;

		this.panel.setAttribute('aria-hidden', 'true');
		this.panel.classList.remove(this.config.classes.open);

		document.body.classList.remove(this.config.classes.bodyOpen);
		window.LenisManager?.resume();
		requestAnimationFrame(() => window.LenisManager?.resize());

		this._removeFocusTrap();
		window.emit('global:size-guide-close', {});
	},

	/**
	 * Deplace le contenu SSR (visuel produit, schema, textes) dans le drawer.
	 * Rendu par la section dans un conteneur inerte, il n'est deplace qu'une fois.
	 */
	_injectContent() {
		const target = this.panel.querySelector('.mm-size-guide__content');
		if (!target || target.childElementCount) return;

		const source = document.querySelector('#mm-size-guide-content');
		if (!source) return;

		target.appendChild(source.content ? source.content.cloneNode(true) : source);
	},

	/**
	 * Construit le tableau mesures × tailles et surligne la taille cochee sur la PDP.
	 */
	_renderTable() {
		const table = this.panel.querySelector(this.config.selectors.table);
		if (!table) return;

		const { sizes, rows } = this.data;
		const currentSize = document.querySelector(this.config.selectors.selectedSize)?.value || null;
		const currentIndex = currentSize ? sizes.indexOf(currentSize) : -1;

		const thead = document.createElement('thead');
		const headRow = document.createElement('tr');
		// 1re cellule : le toggle cm/in. Il vit dans la coquille (deja cable) et est
		// re-insere ici a chaque rendu — le rechercher sur le panel entier, pas sur la
		// table, car il en a ete detache au rendu precedent.
		const unitCell = document.createElement('th');
		unitCell.setAttribute('scope', 'col');
		const unitToggle = this.panel.querySelector('.mm-size-guide__unit-toggle');
		if (unitToggle) unitCell.appendChild(unitToggle);
		headRow.appendChild(unitCell);

		sizes.forEach((size, i) => {
			const th = document.createElement('th');
			th.setAttribute('scope', 'col');
			th.className = 'text-sm--600';
			if (i === currentIndex) th.classList.add(this.config.classes.cellCurrent);
			th.textContent = size;
			headRow.appendChild(th);
		});
		thead.appendChild(headRow);

		const tbody = document.createElement('tbody');
		rows.forEach((row) => {
			const tr = document.createElement('tr');
			const th = document.createElement('th');
			th.setAttribute('scope', 'row');
			th.className = 'text-sm';
			th.textContent = `${row.letter} — ${row.label}`;
			tr.appendChild(th);

			row.values.forEach((value, i) => {
				const td = document.createElement('td');
				td.className = 'text-sm';
				if (i === currentIndex) td.classList.add(this.config.classes.cellCurrent);
				if (value === null) {
					td.classList.add(this.config.classes.valueEmpty);
					td.textContent = '—';
				} else {
					td.textContent = this._formatValue(value);
				}
				tr.appendChild(td);
			});
			tbody.appendChild(tr);
		});

		table.replaceChildren(thead, tbody);
	},

	/**
	 * @param {number} cm - Mesure en centimetres (unite de stockage)
	 * @returns {string} Valeur affichee dans l'unite courante
	 */
	_formatValue(cm) {
		if (this.unit === 'in') {
			// 1 decimale suffit en pouces, sans zero inutile (25.0 → 25)
			return String(Math.round((cm / 2.54) * 10) / 10);
		}
		return String(Math.round(cm * 10) / 10);
	},

	_toggleUnit() {
		this.unit = this.unit === 'cm' ? 'in' : 'cm';

		const isInches = this.unit === 'in';
		const unitSwitch = this.panel.querySelector(this.config.selectors.unitSwitch);
		unitSwitch?.setAttribute('aria-checked', String(isInches));

		this.panel
			.querySelector(this.config.selectors.unitCm)
			?.classList.toggle(this.config.classes.unitActive, !isInches);
		this.panel
			.querySelector(this.config.selectors.unitIn)
			?.classList.toggle(this.config.classes.unitActive, isInches);

		this._renderTable();
	},

	/**
	 * Piege le focus dans le drawer (WCAG 2.4.3) et memorise le declencheur.
	 */
	_setupFocusTrap(trigger) {
		const panel = this.panel;
		const handler = (e) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				const returnTo = this.focusTrap?.trigger;
				this.close();
				returnTo?.focus();
				return;
			}
			if (e.key !== 'Tab') return;

			const focusable = Array.from(panel.querySelectorAll(this.config.focusableSelector)).filter(
				(el) => el.offsetParent !== null
			);
			if (!focusable.length) return;

			const first = focusable[0];
			const last = focusable[focusable.length - 1];

			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', handler);
		this.focusTrap = { handler, trigger: trigger || document.activeElement };

		// Masque le contenu de fond aux lecteurs d'ecran
		document.querySelectorAll('main, header, footer').forEach((el) => {
			if (!panel.contains(el)) el.inert = true;
		});

		panel.querySelector(this.config.selectors.close)?.focus();
	},

	_removeFocusTrap() {
		if (!this.focusTrap) return;

		document.removeEventListener('keydown', this.focusTrap.handler);
		document.querySelectorAll('[inert]').forEach((el) => {
			el.inert = false;
		});

		this.focusTrap.trigger?.focus();
		this.focusTrap = null;
	}
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => SizeGuideManager.init());
} else {
	SizeGuideManager.init();
}

window.SizeGuideManager = SizeGuideManager;
